<template>
  <div class="chats">
    <div
      v-if="!allMessagesLoaded"
      class="d-flex justify-content-center"
    >
      <b-button
        pill
        variant="outline-secondary"
        size="sm"
        :disabled="loading"
        @click="$emit('loadMore')"
      >
        {{ $t('Load more') }}
      </b-button>
    </div>
    <div
      v-for="(msgGrp, index) in formattedChatData.formattedChatLog"
      :key="msgGrp.fromId + String(index)"
      class="chat"
    >
      <h5
        v-if="isShowDate(msgGrp.messages[0].dateSent, index)"
        class="chat-date"
      >
        {{ getDate(msgGrp.messages[0].dateSent) }}
      </h5>
      <div
        class="chat-container"
        :class="{'chat-left': msgGrp.fromId === formattedChatData.contact.id}"
      >
        <div
          v-if="msgGrp.fromId === formattedChatData.contact.id"
          class="chat-avatar"
        >
          <b-avatar
            size="36"
            class="avatar-border-2 box-shadow-1"
            variant="transparent"
            :src="msgGrp.fromId === formattedChatData.contact.id ? formattedChatData.contact.avatar : profileUserAvatar"
          />
        </div>
        <div class="chat-body">
          <div
            v-for="(msgData, i) in msgGrp.messages"
            :key="i"
            class="chat-content"
            :class="{
              'bg-none': msgData.type === 'CHAT_GIFT' || msgData.type === 'CHAT_CALL',
              'bs-none': msgData.type === 'CHAT_GIFT' || msgData.type === 'CHAT_CALL'
            }"
          >
            <!-- RICH_CONTENT_V1 - Zengin içerik kartı -->
            <div
              v-if="msgData.type === 'RICH_CONTENT_V1'"
              class="chat-message rich-card"
              :class="[
                `rich-card--${getRichLayout(msgData)}`,
                {
                  'rich-card--has-media': hasRenderableRichMedia(msgData),
                  'rich-card--request': isIncomingMediaRequest(msgData),
                },
              ]"
            >
              <div
                v-if="isIncomingMediaRequest(msgData)"
                class="media-request-card"
              >
                <div class="media-request-card__icon">
                  <feather-icon
                    :icon="getMediaRequestType(msgData) === 'video' ? 'VideoIcon' : 'CameraIcon'"
                    size="24"
                  />
                </div>
                <div class="media-request-card__content">
                  <strong>{{ getMediaRequestTitle(msgData) }}</strong>
                  <span>{{ getMediaRequestSubtitle(msgData) }}</span>
                </div>
                <button
                  class="media-request-card__button"
                  :disabled="mediaRequestUploadLoading"
                  @click="openMediaRequestUpload(msgData)"
                >
                  <b-spinner
                    v-if="mediaRequestUploadLoading && isUploadingMediaRequest(msgData)"
                    small
                  />
                  <template v-else>{{ getMediaRequestButtonText(msgData) }}</template>
                </button>
              </div>

              <!-- Üst görsel (varsa) -->
              <div
                v-if="getPrimaryAttachment(msgData) && !isOutgoingMediaRequestFulfillment(msgData) && !isIncomingMediaRequest(msgData)"
                class="rich-card__media"
              >
                <!-- Blur/kilit ekranı: media_request.locked veya eski blur_url varsa -->
                <div
                  v-if="isMediaLocked(msgData)"
                  class="rich-card__locked"
                >
                  <b-img
                    :src="getPrimaryAttachment(msgData).url"
                    class="rich-card__image rich-card__image--blurred"
                    @load="$emit('image-loaded')"
                  />
                  <div
                    class="rich-card__lock-overlay"
                    @click="openAiUnlockModal(msgData)"
                  >
                    <feather-icon
                      icon="LockIcon"
                      size="32"
                      class="rich-card__lock-icon"
                    />
                  </div>
                </div>
                <!-- Açık video -->
                <div
                  v-else-if="getPrimaryAttachment(msgData).type === 'video'"
                  class="rich-card__video-wrap"
                  @click.stop="openLightbox(getPrimaryAttachment(msgData))"
                >
                  <video
                    :src="getPrimaryAttachment(msgData).url"
                    class="rich-card__video--unlocked"
                    autoplay
                    loop
                    playsinline
                    :muted="videoMuted"
                    disablepictureinpicture
                    @contextmenu.prevent
                    @loadeddata="$emit('image-loaded')"
                  />
                  <button
                    class="rich-card__video-mute-btn"
                    @click.stop="videoMuted = !videoMuted"
                  >
                    <feather-icon
                      :icon="videoMuted ? 'VolumeXIcon' : 'Volume2Icon'"
                      size="16"
                    />
                  </button>
                </div>
                <!-- Açık görsel -->
                <b-img
                  v-else-if="getPrimaryAttachment(msgData).type === 'image'"
                  :src="getPrimaryAttachment(msgData).thumbnail || getPrimaryAttachment(msgData).url"
                  class="rich-card__image rich-card__image--unlocked rich-card__image--clickable"
                  @load="$emit('image-loaded')"
                  @click.stop="openLightbox(getPrimaryAttachment(msgData))"
                />
                <div
                  v-else
                  class="rich-card__media-placeholder"
                >
                  {{ getPrimaryAttachment(msgData).type.toUpperCase() }}
                </div>
              </div>

              <!-- Metin gövdesi -->
              <div
                v-if="!isIncomingMediaRequest(msgData)"
                class="rich-card__body"
              >
                <div
                  class="rich-card__text"
                  @click="onRichBodyClick($event, msgData)"
                  v-html="renderRichBody(msgData)"
                />
              </div>

              <!-- Aksiyon butonları -->
              <div
                v-if="getNonFeedbackActions(msgData).length && !isIncomingMediaRequest(msgData)"
                class="rich-card__actions"
              >
                <b-button
                  v-for="action in getNonFeedbackActions(msgData)"
                  :key="action.id"
                  size="sm"
                  class="rich-card__action-btn"
                  :variant="getActionVariant(action)"
                  @click="handleRichAction(action, msgData)"
                >
                  {{ action.label }}
                </b-button>
              </div>

              <!-- AI Feedback butonları (REPLY_DATA + AI_FEEDBACK) -->
              <div
                v-if="getFeedbackActions(msgData).length && !dismissedFeedbacks[msgData.msgId]"
                class="rich-card__feedback-actions"
              >
                <b-button
                  v-for="action in getFeedbackActions(msgData)"
                  :key="action.id"
                  size="sm"
                  class="rich-card__feedback-btn"
                  variant="outline-secondary"
                  @click="handleFeedbackAction(action, msgData)"
                >
                  {{ action.label }}
                </b-button>
              </div>
            </div>

            <!-- Normal chat mesajları -->
            <div
              v-else-if="msgData.type === 'CHAT_MSG' && formattedChatData.contact.isAssistant && msgData.popup"
              class="chat-message"
              @click="updatePopup(msgData)"
            >
              <div
                class="text-underline cursor-pointer"
                v-html="formatMessageWithLinks(msgData.message, formattedChatData.contact.isAssistant)"
              />
              <div v-if="!formattedChatData.contact.isAssistant && translates[msgData.logIndex].showTranslate">
                <div>-------</div>
                <div
                  class="text-underline cursor-pointer"
                  v-html="formatMessageWithLinks(translates[msgData.logIndex].translatedMessage, formattedChatData.contact.isAssistant)"
                />
              </div>
            </div>
            <div
              v-else-if="msgData.type === 'CHAT_MSG'"
              class="chat-message"
            >
              <div v-html="formatMessageWithLinks(msgData.message, formattedChatData.contact.isAssistant)" />
              <div v-if="!formattedChatData.contact.isAssistant && translates[msgData.logIndex].showTranslate">
                <div>-------</div>
                <div v-html="formatMessageWithLinks(translates[msgData.logIndex].translatedMessage, formattedChatData.contact.isAssistant)" />
              </div>
            </div>

            <!-- Arama mesajları (WhatsApp tarzı bubble içi) -->
            <div
              v-if="msgData.type === 'CHAT_CALL' && msgData.callPayload"
              class="chat-call-bubble"
              :class="[
                `chat-call-bubble--${msgData.callPayload.status}`,
                msgGrp.fromId === formattedChatData.contact.id ? 'chat-call-bubble--left' : 'chat-call-bubble--right'
              ]"
            >
              <!-- İkon dairesi + yön badge -->
              <div class="chat-call-bubble__icon-wrap">
                <div class="chat-call-bubble__icon">
                  <feather-icon
                    :icon="getCallMainIcon(msgData.callPayload)"
                    size="20"
                  />
                </div>
                <div class="chat-call-bubble__badge">
                  <feather-icon :icon="getCallBadgeIcon(msgData.callPayload)" size="8" />
                </div>
              </div>

              <!-- Metin -->
              <div class="chat-call-bubble__content">
                <div class="chat-call-bubble__title">
                  {{ isVideoCall(msgData.callPayload) ? $t('Video Call') : $t('Voice Call') }}
                </div>
                <div class="chat-call-bubble__sub">
                  {{ getCallStatusText(msgData.callPayload) }}
                </div>
              </div>
            </div>

            <!-- Hediye satışı kaldırıldı — hediye mesajı render'ı devre dışı
            <b-img
              v-if="msgData.type === 'CHAT_GIFT' && msgData.gift"
              :src="getImgGift(msgData.gift.image)"
              width="42"
            />
            -->

            <div class="chat-footer">
              <span
                v-if="false && !formattedChatData.contact.isAssistant && msgGrp.fromId === formattedChatData.contact.id && translates[msgData.logIndex].translatedMessage"
                class="chat-translate"
                @click="updateShowTranslate(msgData.logIndex)"
              >
                {{ translates[msgData.logIndex].showTranslate ? $t('Original') : $t('Translate') }}
              </span>
              <span>
                {{ getTime(msgData.dateSent) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fotoğraf/video görüntüleyici: popup (b-modal) olarak sayfanın en üst seviyesinde açılır.
         Önceden sohbetin içinde çiziliyordu; sohbet kartındaki backdrop-filter sabit konumlu katmanı kartın
         içine hapsettiği için medya ekranı kaplamak yerine sohbet detayının içinde açılıyordu.
         Çarpı, dışarı tıklama ve Esc ile kapanır. -->
    <b-modal
      :visible="!!lightbox"
      centered
      hide-header
      hide-footer
      size="xl"
      modal-class="chat-media-modal"
      content-class="chat-media-modal__content"
      body-class="chat-media-modal__body"
      @hidden="closeLightbox"
    >
      <button
        type="button"
        class="lightbox-close"
        :aria-label="$t('Close')"
        @click="closeLightbox"
      >
        <feather-icon
          icon="XIcon"
          size="24"
        />
      </button>
      <template v-if="lightbox">
        <video
          v-if="lightbox.type === 'video'"
          :src="lightbox.url"
          class="lightbox-media"
          autoplay
          loop
          playsinline
          controls
          disablepictureinpicture
          @contextmenu.prevent
        />
        <img
          v-else
          :src="lightbox.url || lightbox.thumbnail"
          class="lightbox-media"
          alt=""
        >
      </template>
    </b-modal>

    <!-- Typing indicator -->
    <div
      v-if="isTyping"
      class="chat chat-left typing-indicator-wrapper"
    >
      <div class="chat-container chat-left">
        <div class="chat-avatar">
          <b-avatar
            size="36"
            class="avatar-border-2 box-shadow-1"
            variant="transparent"
            :src="formattedChatData.contact.avatar"
          />
        </div>
        <div class="chat-body">
          <div class="chat-content typing-indicator">
            <span /><span /><span />
          </div>
        </div>
      </div>
    </div>

    <paywall-boost-modal
      v-if="showModalData"
      :profile-image="profileImage"
      :user-name="userName"
      :modal-model="modalBoostFlow"
      :show-modal-data="showModalData"
      @update:modalModel="updateModalBoostFlow"
    />

    <input
      ref="mediaRequestFileInput"
      class="d-none"
      type="file"
      :accept="mediaRequestUploadAccept"
      @change="uploadMediaRequestFile"
    >

    <b-modal
      v-model="aiUnlockModal"
      centered
      hide-header
      hide-footer
      modal-class="ai-unlock-modal"
      content-class="ai-unlock-modal-content"
    >
      <div
        v-if="pendingUnlockMessage"
        class="ai-unlock-paywall"
      >
        <button
          class="ai-unlock-paywall__close"
          :disabled="aiUnlockLoading"
          @click="closeAiUnlockModal"
        >
          <feather-icon icon="XIcon" size="24" />
        </button>

        <div class="ai-unlock-paywall__hero">
          <feather-icon icon="CreditCardIcon" size="34" />
          <span><feather-icon icon="ZapIcon" size="22" /></span>
        </div>

        <h2>{{ $t('Use Diamonds to unlock this content') }}</h2>
        <p>{{ $t('You can unlock this locked content with Diamonds.') }}</p>

        <div class="ai-unlock-paywall__row">
          <div class="ai-unlock-paywall__media-icon">
            <feather-icon
              :icon="pendingUnlockType === 'video' ? 'VideoIcon' : 'ImageIcon'"
              size="26"
            />
          </div>
          <div class="ai-unlock-paywall__row-main">
            <span>{{ $t('This request requires') }}</span>
            <strong>{{ $t('{count} Diamonds', { count: numberFormat(pendingUnlockCost) }) }}</strong>
          </div>
          <div class="ai-unlock-paywall__row-side">
            <strong>{{ pendingUnlockType === 'video' ? $t('Video') : $t('Photo') }}</strong>
            <span>{{ pendingUnlockType === 'video' ? $t('1 video') : $t('1 photo') }}</span>
          </div>
        </div>

        <div class="ai-unlock-paywall__balance">
          <b-img
            :src="iconDiamonds"
            width="48"
            height="48"
          />
          <div>
            <span>{{ $t('Your Diamonds') }}</span>
            <strong>{{ numberFormat(pendingUnlockCurrentCredits) }}</strong>
          </div>
        </div>

        <button
          class="ai-unlock-paywall__cta"
          :disabled="aiUnlockLoading || aiUnlockInfoLoading"
          @click="unlockPendingAiMedia"
        >
          <b-img
            :src="iconDiamonds"
            width="42"
            height="42"
          />
          <span>
            <strong>{{ pendingUnlockInsufficient ? $t('Buy Diamonds') : $t('Unlock Now') }}</strong>
            <small>{{ pendingUnlockInsufficient ? $t('Not enough diamonds') : $t('Continue with {count} Diamonds', { count: numberFormat(pendingUnlockCost) }) }}</small>
            <em>{{ pendingUnlockInsufficient ? $t('Add diamonds to continue') : $t('Get your content instantly!') }}</em>
          </span>
          <b-spinner
            v-if="aiUnlockLoading || aiUnlockInfoLoading"
            small
          />
          <feather-icon
            v-else
            icon="ChevronRightIcon"
            size="28"
          />
        </button>

        <div class="ai-unlock-paywall__note">
          <feather-icon icon="InfoIcon" size="16" />
          {{ $t('Without Premium Access, all premium features are pay-as-you-go.') }}
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAvatar, BButton, BImg, BModal, BSpinner,
} from 'bootstrap-vue'

import moment from 'moment'
import { isToday } from '@core/utils/utils'
import PaywallBoostModal from '@/components/PaywallBoostModal.vue'
import useJwt from '@/auth/jwt/useJwt'
import { isVideoCall, numberFormat, snapshotFile, throwDefaultError } from '@/helpers/helpers'
import i18n from '../../libs/i18n'

// Media-request fulfill kısıtları (backend ile birebir)
const MEDIA_REQUEST_MAX_SIZE = 60 * 1024 * 1024
const MEDIA_REQUEST_IMAGE_TYPES = ['image/jpeg', 'image/png']
const MEDIA_REQUEST_VIDEO_TYPES = ['video/mp4', 'video/quicktime']

export default {
  components: {
    BAvatar,
    BButton,
    BImg,
    BModal,
    BSpinner,
    PaywallBoostModal,
  },
  props: {
    allMessagesLoaded: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
    },
    chatData: {
      type: Object,
      required: true,
    },
    profileUserAvatar: {
      type: String,
      required: true,
    },
    isTyping: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      modalBoostFlow: false,
      showModalData: null,
      translates: {},
      videoMuted: true,
      dismissedFeedbacks: {},
      lightbox: null,
      aiUnlockModal: false,
      aiUnlockLoading: false,
      aiUnlockInfoLoading: false,
      pendingUnlockMessage: null,
      pendingUnlockInfo: null,
      pendingUnlockInsufficient: false,
      mediaRequestUploadMessage: null,
      mediaRequestUploadLoading: false,
      fulfilledMediaRequests: {},
      iconDiamonds: require('@/assets/images/icons/diamonds-1.svg'),
    }
  },
  computed: {
    formattedChatData() {
      const contact = {
        id: this.$props.chatData.contact.id,
        avatar: this.$props.chatData.contact.profile.image,
        isAssistant: this.$props.chatData.contact.isAssistant, // isAssistant bilgisini ekliyoruz
      }

      let chatLog = []
      if (this.$props.chatData.chat) {
        chatLog = this.$props.chatData.chat
      }

      // Hediye satışı kaldırıldı — 'CHAT_GIFT' listeden çıkarıldı
      const filteredChatLog = chatLog.filter(m => m.type === 'CHAT_MSG' || m.type === 'RICH_CONTENT_V1' || m.type === 'CHAT_CALL')

      const formattedChatLog = []

      if (!filteredChatLog.length) {
        return {
          formattedChatLog,
          contact,
          profileUserAvatar: this.$props.profileUserAvatar,
        }
      }

      let chatMessageSenderId = filteredChatLog[0].fromId
      let msgGroup = {
        fromId: chatMessageSenderId,
        messages: [],
      }

      let msgDate = this.getDate(filteredChatLog[0].dateSent)

      filteredChatLog.forEach((msg, index) => {
        const logIndex = `${msg.toId}-${msg.fromId}-${index}`
        const payload = this.normalizeRichPayload(msg.payload, msg.message)
        const mediaRequestBody = this.getMediaRequestBody(msg)
        const mediaRequest = payload?.meta?.media_request || payload?.meta?.mediaRequest || {}
        this.translates[logIndex] = {
          showTranslate: !!msg.translated_content,
          translatedMessage: msg.translated_content,
        }

        const baseMessage = {
          msgId: msg.msgId || null,
          message: msg.message,
          dateSent: msg.dateSent,
          translated_content: msg.translated_content,
          popup: msg.popup,
          logIndex,
          type: msg.type,
          gift: msg.gift,
          request_id: mediaRequest.requestId || mediaRequest.request_id || mediaRequestBody.request_id || null,
          message_id: mediaRequest.messageId || mediaRequest.message_id || mediaRequestBody.message_id || null,
          status: mediaRequest.status || mediaRequestBody.status || null,
          content_type: mediaRequest.contentType || mediaRequest.content_type || mediaRequestBody.content_type || null,
          blur_url: mediaRequest.blurUrl || mediaRequest.blur_url || mediaRequestBody.blur_url || null,
          normal_url: mediaRequest.normalUrl || mediaRequest.normal_url || mediaRequestBody.normal_url || null,
          thumbnail_url: mediaRequest.thumbnailUrl || mediaRequest.thumbnail_url || mediaRequestBody.thumbnail_url || null,
          payload,
          callPayload: msg.callPayload || null,
          fromId: msg.fromId,
        }

        const isCall = msg.type === 'CHAT_CALL'
        const prevIsCall = msgGroup.messages.length > 0 && msgGroup.messages[msgGroup.messages.length - 1].type === 'CHAT_CALL'
        const sameGroup = chatMessageSenderId === msg.fromId && this.getDate(msg.dateSent) === msgDate && !isCall && !prevIsCall

        if (sameGroup) {
          msgGroup.messages.push(baseMessage)
        } else {
          if (msgGroup.messages.length > 0) {
            formattedChatLog.push(msgGroup)
          }
          msgDate = this.getDate(msg.dateSent)
          chatMessageSenderId = msg.fromId
          msgGroup = {
            fromId: msg.fromId,
            messages: [baseMessage],
          }
        }

        if (index === filteredChatLog.length - 1) {
          formattedChatLog.push(msgGroup)
        }
      })

      return {
        formattedChatLog,
        contact,
        profileUserAvatar: this.$props.profileUserAvatar,
      }
    },
    profileImage() {
      return this.$store.getters['auth/profileImage']
    },
    userName() {
      return this.$store.getters['auth/userName']
    },
    currentUserId() {
      return this.$store.getters['auth/userId'] || this.$store.getters['auth/user']?.id || this.$store.getters['auth/user']?.uuid || null
    },
    currentUserIds() {
      const user = this.$store.getters['auth/user'] || {}
      return [
        this.$store.getters['auth/userId'],
        user.id,
        user.uuid,
        user.userId,
        user.userUuid,
      ].filter(Boolean).map(String)
    },
    fulfilledMediaRequestIdsFromChat() {
      const ids = new Set()
      const chatLog = this.$props.chatData.chat || []

      chatLog.forEach(rawMsg => {
        if (rawMsg.type !== 'RICH_CONTENT_V1') return

        const payload = this.normalizeRichPayload(rawMsg.payload, rawMsg.message)
        const firstAttachment = Array.isArray(payload.attachments) ? payload.attachments[0] : null
        const mediaRequest = payload?.meta?.media_request
          || payload?.meta?.mediaRequest
          || payload?.media_request
          || payload?.mediaRequest
          || firstAttachment?.meta?.media_request
          || firstAttachment?.meta?.mediaRequest

        const role = mediaRequest?.role || mediaRequest?.requestRole || mediaRequest?.request_role
        const requestId = mediaRequest?.requestId
          || mediaRequest?.request_id
          || mediaRequest?.media_request_id
          || mediaRequest?.mediaRequestId
          || rawMsg.request_id

        if (role === 'fulfillment' && requestId && this.currentUserIds.includes(String(rawMsg.fromId))) {
          ids.add(String(requestId))
        }
      })

      return ids
    },
    userCredits() {
      return this.$store.getters['auth/credits'] || 0
    },
    pendingUnlockAttachment() {
      return this.pendingUnlockMessage ? this.getPrimaryAttachment(this.pendingUnlockMessage) : null
    },
    pendingUnlockType() {
      return this.pendingUnlockAttachment?.type === 'video' ? 'video' : 'image'
    },
    pendingUnlockCost() {
      // Doküman: fiyat MEDIA_REQUEST_UNLOCK action'ının params.price alanında gelir
      const action = this.pendingUnlockMessage
        ? this.getMediaRequestAction(this.pendingUnlockMessage, 'MEDIA_REQUEST_UNLOCK')
        : null
      const actionPrice = Number(action?.params?.price)
      if (actionPrice > 0) return actionPrice

      const info = this.pendingUnlockInfo || {}
      const meta = this.pendingUnlockAttachment?.meta || {}
      return Number(info.requiredCredits || meta.credits || meta.credit || meta.price || meta.cost || (this.pendingUnlockType === 'video' ? 250 : 50))
    },
    pendingUnlockCurrentCredits() {
      const info = this.pendingUnlockInfo || {}
      return Number(info.currentCredits || this.userCredits || 0)
    },
    canFulfillMediaRequests() {
      const user = this.$store.getters['auth/user'] || {}
      return !!(user.source || user.sourceId || user.source_id || user.profile?.source || user.profile?.sourceId || user.freeCallUser)
    },
    mediaRequestUploadAccept() {
      const types = this.getMediaRequestType(this.mediaRequestUploadMessage) === 'video'
        ? MEDIA_REQUEST_VIDEO_TYPES
        : MEDIA_REQUEST_IMAGE_TYPES
      return types.join(',')
    },
  },
  watch: {
    modalBoostFlow(value) {},
  },
  methods: {
    isVideoCall,
    normalizeRichPayload(payload, message = null) {
      if (payload && typeof payload === 'object') return payload

      const parseJson = value => {
        if (!value || typeof value !== 'string') return null
        try {
          const parsed = JSON.parse(value)
          return parsed && typeof parsed === 'object' ? parsed : null
        } catch (e) {
          return null
        }
      }

      return parseJson(payload) || parseJson(message) || payload || null
    },
    safeDebugClone(value) {
      try {
        return JSON.parse(JSON.stringify(value || {}))
      } catch (e) {
        return value
      }
    },
    getDate(date) {
      if (!date) return ''
      if (isToday(new Date(date))) return this.$t('Today')
      return moment(date).locale(i18n.locale).format('DD.MM.YYYY dddd')
    },
    getImgGift(name) {
      return require(`@/assets/images/gifts/${name}`)
    },
    getTime(date) {
      return moment(date).format('HH:mm')
    },
    isShowDate(date, index) {
      // İlk grup için her zaman tarih göster
      if (index === 0) return true

      const currentDate = moment(date).format('DD.MM.YYYY')

      // Bir önceki grubun ilk mesajının tarihini al
      const prevGroup = this.formattedChatData.formattedChatLog[index - 1]
      if (!prevGroup || !prevGroup.messages || !prevGroup.messages.length) return true

      const prevDate = moment(prevGroup.messages[0].dateSent).format('DD.MM.YYYY')

      // Gün değiştiyse tarih başlığını göster
      return currentDate !== prevDate
    },
    updatePopup(data) {
      if (data.popup) {
        try {
          const popup = JSON.parse(data.popup)

          this.showModalData = null
          this.modalBoostFlow = false

          if (popup) {
            this.showModalData = popup
            this.modalBoostFlow = true
          }
        } catch (error) {
          console.log('error', error)
        }
      }
    },
    updateShowTranslate(logIndex) {
      console.log(logIndex)
      this.$set(this.translates, logIndex, {
        ...this.translates[logIndex],
        showTranslate: !this.translates[logIndex].showTranslate,
      })
      this.$forceUpdate()
    },
    // Link tespiti ve formatlaması için method
    formatMessageWithLinks(message, isAssistant) {
      if (!message) return ''

      if (!isAssistant) return message

      // URL regex pattern - http(s):// veya www. ile başlayanlar
      const urlRegex = /(https?:\/\/[^\s<]+|www\.[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}[^\s<]*)/gi

      return message.replace(urlRegex, rawMatch => {
        let url = rawMatch
        let suffix = ''

        const trailingPunctuations = ').,!?]}'

        while (url.length > 0 && trailingPunctuations.includes(url.charAt(url.length - 1))) {
          suffix = url.charAt(url.length - 1) + suffix
          url = url.slice(0, -1)
        }
        if (url.endsWith(')')) {
          suffix = ')' + suffix
          url = url.slice(0, -1)
        }

        if (!url) {
          return rawMatch
        }

        let formattedUrl = url
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          formattedUrl = `https://${url}`
        }

        return `<a href="${formattedUrl}" target="_blank" rel="noopener noreferrer" class="external-link">${url}<i class="fas fa-external-link-alt"></i></a>${suffix}`
      })
    },
    // RICH_CONTENT_V1 gövdesini entity'lere göre formatla.
    // Çakışan entity'lerde metin tek yazılır, hepsi birlikte uygulanır (bold + PRODUCT vb.).
    renderRichBody(msg) {
      const body = msg.payload && msg.payload.body

      if (!body || !body.text) {
        return this.escapeHtml(this.getMediaRequestFallbackText(msg) || msg.message || '')
      }

      const { text } = body
      if (!text) {
        return this.escapeHtml(this.getMediaRequestFallbackText(msg) || msg.message || '')
      }
      const entities = Array.isArray(body.entities) ? [...body.entities] : []

      if (!entities.length) {
        return this.escapeHtml(text)
      }

      // Tüm entity sınırlarını topla (offset ve offset+length)
      const bounds = new Set([0, text.length])
      entities.forEach(e => {
        bounds.add(e.offset)
        bounds.add(e.offset + e.length)
      })
      const sortedBounds = [...bounds].sort((a, b) => a - b)

      let result = ''
      for (let i = 0; i < sortedBounds.length - 1; i++) {
        const segStart = sortedBounds[i]
        const segEnd = sortedBounds[i + 1]
        const segmentText = text.slice(segStart, segEnd)
        if (!segmentText) continue

        // Bu segmenti tam kapsayan entity'leri bul (çakışanlar dahil)
        const covering = entities.filter(
          e => e.offset <= segStart && e.offset + e.length >= segEnd
        )

        let html = this.escapeHtml(segmentText)

        // Hepsini uygula: önce iç etiketler (PROFILE, PRODUCT, link), sonra bold/italic
        const hasLink = covering.find(e => e.type === 'link')
        const hasProduct = covering.find(e => e.type === 'PRODUCT')
        const hasProfile = covering.find(e => e.type === 'PROFILE')
        const hasBold = covering.find(e => e.type === 'bold')
        const hasItalic = covering.find(e => e.type === 'italic')

        if (hasProfile) {
          const uuid = hasProfile.uuid || hasProfile.id || ''
          const userName = hasProfile.user_name || hasProfile.username || ''
          const dataUuid = uuid ? ` data-uuid="${this.escapeHtml(uuid)}"` : ''
          const dataUserName = userName ? ` data-username="${this.escapeHtml(userName)}"` : ''
          // Profil entity'sini de PRODUCT ile aynı pembe renkte göster
          html = `<span class="rc-entity-profile"${dataUuid}${dataUserName} style="font-weight:700;color:#f9447a;cursor:pointer;text-decoration:underline;">${html}</span>`
        }
        if (hasProduct) {
          const url = hasProduct.url || null
          const dataAttr = url ? ` data-url="${this.escapeHtml(url)}"` : ''
          html = `<span class="rc-entity-product"${dataAttr} style="font-weight:700;color:#f9447a;text-decoration:underline;cursor:pointer;">${html}</span>`
        }
        if (hasLink) {
          const url = hasLink.url || html
          let href = url
          if (!href.startsWith('http://') && !href.startsWith('https://')) {
            href = `https://${href}`
          }
          html = `<a href="${href}" target="_blank" rel="noopener noreferrer" class="external-link" style="font-weight:700;color:#f9447a;text-decoration:underline;cursor:pointer;">${html}</a>`
        }
        if (hasItalic) html = `<em>${html}</em>`
        if (hasBold) html = `<strong>${html}</strong>`

        result += html
      }

      return result
    },
    // Backend body.text göndermediğinde kendi media-request mesajlarım için yedek metin.
    // İki taraf farklı ifade görür: isteği gönderen "istek gönderildi",
    // içeriği gönderen (creator) "gönderildi".
    getMediaRequestFallbackText(msg) {
      if (!this.currentUserIds.includes(String(msg?.fromId))) return ''

      const isVideo = this.getMediaRequestType(msg) === 'video'
      const role = this.getMediaRequestRole(msg)

      if (role === 'request') {
        return isVideo ? this.$t('Video request sent') : this.$t('Photo request sent')
      }

      if (role === 'fulfillment') {
        return isVideo ? this.$t('Video sent') : this.$t('Photo sent')
      }

      return ''
    },
    // payload.meta.media_request.role — dokümandaki kalıcı alan
    getMediaRequestRole(msg) {
      const mediaRequest = this.getMediaRequest(msg)
      if (!mediaRequest) return null
      return mediaRequest.role || mediaRequest.requestRole || mediaRequest.request_role || null
    },
    // Aksiyon butonları geçicidir (işlem bitince kalkar); intent üzerinden ayrıştırılır
    getMediaRequestAction(msg, intent) {
      return this.getRichActions(msg).find(
        a => a && a.params && a.params.intent === intent,
      ) || null
    },
    // Kendi gönderdiğim, karşılanmış içerik mesajı (creator tarafı) — kilit gösterilmez
    isOutgoingMediaRequestFulfillment(msg) {
      if (!this.currentUserIds.includes(String(msg?.fromId))) return false
      return this.getMediaRequestRole(msg) === 'fulfillment'
    },
    isOutgoingMediaRequest(msg) {
      if (!this.currentUserIds.includes(String(msg?.fromId))) return false
      return this.getMediaRequestRole(msg) === 'request'
    },
    getUnlockCost(msg) {
      const action = this.getMediaRequestAction(msg, 'MEDIA_REQUEST_UNLOCK')
      const actionPrice = Number(action?.params?.price)
      if (actionPrice > 0) return this.numberFormat(actionPrice)

      const meta = this.getPrimaryAttachment(msg)?.meta || {}
      const cost = Number(meta.credits || meta.credit || meta.price || meta.cost || (this.getMediaRequestType(msg) === 'video' ? 1000 : 500))
      return this.numberFormat(cost)
    },
    isMediaRequestFulfilled(msg) {
      const requestId = this.getMediaRequestId(msg)
      if (requestId && (
        this.fulfilledMediaRequests[String(requestId)]
        || this.fulfilledMediaRequestIdsFromChat.has(String(requestId))
      )) return true

      const mediaRequest = this.getMediaRequest(msg) || {}
      const status = String(mediaRequest.status || msg?.status || '').toLowerCase()
      return ['fulfilled', 'completed', 'sent'].includes(status)
    },
    // Bana gelen ve benim karşılamam gereken istek mesajı (creator tarafı)
    isIncomingMediaRequest(msg) {
      if (this.getMediaRequestRole(msg) !== 'request') return false
      if (this.currentUserIds.includes(String(msg?.fromId))) return false
      if (this.isMediaRequestFulfilled(msg)) return false

      // Backend fulfill butonunu yalnızca creator'a gönderir; bu en güvenilir sinyal.
      // Buton düşmediyse (eski payload) mevcut creator kontrolüne düş.
      if (Array.isArray(msg?.payload?.actions)) {
        return !!this.getMediaRequestAction(msg, 'MEDIA_REQUEST_FULFILL')
      }

      return this.canFulfillMediaRequests
    },
    getMediaRequestType(msg) {
      const mediaRequest = this.getMediaRequest(msg) || {}
      const mediaType = mediaRequest.mediaType || mediaRequest.media_type || mediaRequest.content_type || mediaRequest.contentType
      if (mediaType === 'video') return 'video'
      return 'photo'
    },
    getMediaRequestTitle(msg) {
      const name = this.formattedChatData.contact?.userName || this.$t('User')
      if (this.getMediaRequestType(msg) === 'video') return this.$t('{name} requested a video from you.', { name })
      return this.$t('{name} requested a photo from you.', { name })
    },
    getMediaRequestSubtitle(msg) {
      if (this.getMediaRequestType(msg) === 'video') return this.$t('Send a video to this chat.')
      return this.$t('Send a photo to this chat.')
    },
    getMediaRequestButtonText(msg) {
      if (this.getMediaRequestType(msg) === 'video') return this.$t('Send Video')
      return this.$t('Send Photo')
    },
    isUploadingMediaRequest(msg) {
      const current = this.mediaRequestUploadMessage
      if (!current) return false
      return this.getMediaRequestId(current) === this.getMediaRequestId(msg)
    },
    openMediaRequestUpload(msg) {
      if (this.mediaRequestUploadLoading) return

      if (!this.getMediaRequestId(msg)) {
        throwDefaultError(this.$toast, new Error(this.$t('Content unlock information could not be read')))
        return
      }

      this.mediaRequestUploadMessage = msg
      this.$nextTick(() => {
        this.$refs.mediaRequestFileInput?.click()
      })
    },
    async uploadMediaRequestFile(event) {
      const input = event.target
      const picked = input.files && input.files[0]
      const msg = this.mediaRequestUploadMessage

      // input'u her koşulda sıfırla; aynı dosya tekrar seçilebilsin
      input.value = ''

      if (!picked || !msg) {
        this.mediaRequestUploadMessage = null
        return
      }

      const requestId = this.getMediaRequestId(msg)
      const type = this.getMediaRequestType(msg)
      const allowedTypes = type === 'video' ? MEDIA_REQUEST_VIDEO_TYPES : MEDIA_REQUEST_IMAGE_TYPES

      if (picked.type && !allowedTypes.includes(picked.type)) {
        this.mediaRequestUploadMessage = null
        throwDefaultError(this.$toast, new Error(this.$t('Unsupported file type.')))
        return
      }

      if (picked.size > MEDIA_REQUEST_MAX_SIZE) {
        this.mediaRequestUploadMessage = null
        throwDefaultError(this.$toast, new Error(this.$t('File is too large. Maximum size is {size}MB.', { size: MEDIA_REQUEST_MAX_SIZE / (1024 * 1024) })))
        return
      }

      this.mediaRequestUploadLoading = true

      try {
        // Fotoğrafta EXIF/oryantasyon normalizasyonu; videoyu olduğu gibi gönder
        const file = type === 'video' ? picked : await snapshotFile(picked)
        const formData = new FormData()
        formData.append('file', file, file.name || picked.name || 'upload')

        await useJwt.chatMediaRequestFulfill(requestId, formData)

        // Sunucu bir sonraki fetch'te action'ı göndermeyecek; kartı hemen gizle
        this.$set(this.fulfilledMediaRequests, String(requestId), true)
        this.removeMediaRequestFulfillAction(msg)
      } catch (error) {
        throwDefaultError(this.$toast, error)
      } finally {
        this.mediaRequestUploadLoading = false
        this.mediaRequestUploadMessage = null
      }
    },
    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    // RICH_CONTENT_V1 layout bilgisi
    getRichLayout(msg) {
      const layout = msg.payload && msg.payload.layout
      if (layout === 'CAROUSEL') return 'carousel'
      if (layout === 'OVERLAY') return 'overlay'
      return 'stacked'
    },
    hasRenderableRichMedia(msg) {
      return !!this.getPrimaryAttachment(msg)
        && !this.isOutgoingMediaRequestFulfillment(msg)
        && !this.isIncomingMediaRequest(msg)
    },
    getMediaRequest(msg) {
      const payload = msg?.payload || {}
      const firstAttachment = Array.isArray(payload.attachments) ? payload.attachments[0] : null

      const mediaRequest = payload?.meta?.media_request
        || payload?.meta?.mediaRequest
        || payload?.media_request
        || payload?.mediaRequest
        || firstAttachment?.meta?.media_request
        || firstAttachment?.meta?.mediaRequest
        || this.findMediaRequestDeep(payload)

      if (mediaRequest) return mediaRequest

      if (msg?.request_id && (msg?.status || msg?.content_type || msg?.blur_url || msg?.normal_url)) {
        return {
          request_id: msg.request_id,
          message_id: msg.message_id,
          locked: msg.status ? msg.status === 'locked' : true,
          status: msg.status,
          content_type: msg.content_type,
          blur_url: msg.blur_url,
          normal_url: msg.normal_url,
          thumbnail_url: msg.thumbnail_url,
        }
      }

      return null
    },
    getMediaRequestBody(msg) {
      const body = msg?.body && typeof msg.body === 'object' ? msg.body : {}

      return {
        ...body,
        request_id: msg?.request_id || body.request_id,
        message_id: msg?.message_id || body.message_id,
        status: msg?.status || body.status,
        content_type: msg?.content_type || body.content_type,
        blur_url: msg?.blur_url || body.blur_url,
        normal_url: msg?.normal_url || body.normal_url,
        thumbnail_url: msg?.thumbnail_url || body.thumbnail_url,
      }
    },
    findValueDeep(source, keys) {
      if (!source || typeof source !== 'object') return null

      const queue = [source]
      const seen = new Set()

      while (queue.length) {
        const current = queue.shift()
        if (!current || typeof current !== 'object' || seen.has(current)) continue
        seen.add(current)

        const matchKey = keys.find(key => current[key])
        if (matchKey) return current[matchKey]

        Object.keys(current).forEach(key => {
          const value = current[key]
          if (value && typeof value === 'object') queue.push(value)
        })
      }

      return null
    },
    findMediaRequestDeep(source) {
      if (!source || typeof source !== 'object') return null

      const queue = [source]
      const seen = new Set()

      while (queue.length) {
        const current = queue.shift()
        if (!current || typeof current !== 'object' || seen.has(current)) continue
        seen.add(current)

        const hasRequestId = current.requestId || current.request_id || current.media_request_id || current.mediaRequestId
        const hasMediaState = Object.prototype.hasOwnProperty.call(current, 'locked')
          || current.status
          || current.blur_url
          || current.normal_url

        if (hasRequestId && hasMediaState) return current

        Object.keys(current).forEach(key => {
          const value = current[key]
          if (value && typeof value === 'object') queue.push(value)
        })
      }

      return null
    },
    getMediaRequestId(msg) {
      const mediaRequest = this.getMediaRequest(msg)
      const payload = msg?.payload || {}
      const firstAttachment = Array.isArray(payload.attachments) ? payload.attachments[0] : null
      const payloadMeta = payload?.meta || {}
      const attachmentMeta = firstAttachment?.meta || {}

      return mediaRequest?.requestId
        || mediaRequest?.request_id
        || mediaRequest?.media_request_id
        || mediaRequest?.mediaRequestId
        || mediaRequest?.id
        || msg?.request_id
        || msg?.requestId
        || msg?.media_request_id
        || msg?.mediaRequestId
        || payload?.requestId
        || payload?.request_id
        || payload?.media_request_id
        || attachmentMeta?.requestId
        || attachmentMeta?.request_id
        || attachmentMeta?.media_request_id
        || attachmentMeta?.mediaRequestId
        || payloadMeta?.requestId
        || payloadMeta?.request_id
        || payloadMeta?.media_request_id
        || payloadMeta?.mediaRequestId
        || firstAttachment?.requestId
        || firstAttachment?.request_id
        || firstAttachment?.media_request_id
        || firstAttachment?.mediaRequestId
        || this.findValueDeep(payload, ['requestId', 'request_id', 'media_request_id', 'mediaRequestId'])
        || null
    },
    getMediaRequestContentType(mediaRequest) {
      const contentType = mediaRequest?.content_type || mediaRequest?.contentType || mediaRequest?.media_type || mediaRequest?.mediaType
      if (String(contentType).toLowerCase() === 'video') return 'video'
      return 'image'
    },
    normalizeAttachmentType(type) {
      const normalizedType = String(type || '').toLowerCase()
      if (normalizedType === 'video' || normalizedType.startsWith('video/')) return 'video'
      if (normalizedType === 'photo' || normalizedType === 'image' || normalizedType.startsWith('image/')) return 'image'
      return normalizedType || 'image'
    },
    isMediaLocked(msg) {
      const mediaRequest = this.getMediaRequest(msg)
      if (mediaRequest) return mediaRequest.locked === true
      const att = this.getPrimaryAttachment(msg)
      return !!(att?.meta && att.meta.blur_url)
    },
    // RICH_CONTENT_V1 ana görseli. Yeni media_request şemasına öncelik verilir.
    getPrimaryAttachment(msg) {
      const mediaRequest = this.getMediaRequest(msg)

      // İstek mesajının ("role: request") içeriği yoktur — boş görsel kutusu çizme
      if (mediaRequest && this.getMediaRequestRole(msg) === 'request') return null

      if (mediaRequest) {
        const locked = mediaRequest.locked === true
        const url = locked ? mediaRequest.blur_url : mediaRequest.normal_url

        // Düz (flat) media-request şeması; yalnızca gerçek bir url varsa attachment üret.
        // Aksi halde payload.attachments'a düş.
        if (url) {
          return {
            type: this.getMediaRequestContentType(mediaRequest),
            url,
            thumbnail: locked ? mediaRequest.blur_url : (mediaRequest.thumbnail_url || url),
            meta: {
              ...mediaRequest,
              blur_url: mediaRequest.blur_url,
            },
          }
        }
      }

      const atts = msg.payload && Array.isArray(msg.payload.attachments) ? msg.payload.attachments : []
      if (!atts.length) return null
      return {
        ...atts[0],
        type: this.normalizeAttachmentType(atts[0].type),
      }
    },
    numberFormat,
    openAiUnlockModal(msgData) {
      const requestId = this.getMediaRequestId(msgData)
      if (!requestId) {
        // eslint-disable-next-line no-console
        console.warn('Media request id not found for locked content', {
          msgData: this.safeDebugClone(msgData),
          mediaRequest: this.safeDebugClone(this.getMediaRequest(msgData)),
          payloadMeta: this.safeDebugClone(msgData?.payload?.meta),
        })
        throwDefaultError(this.$toast, new Error(this.$t('Content unlock information could not be read')))
        return
      }

      this.pendingUnlockMessage = msgData
      this.pendingUnlockInfo = null
      this.pendingUnlockInsufficient = this.userCredits < this.pendingUnlockCost
      this.aiUnlockModal = true
      this.fetchMediaRequestUnlockInfo(requestId)
    },
    applyMediaRequestUnlockInfo(response) {
      const error = response?.data?.header?.error
      const body = response?.data?.body || {}

      const hasCreditInfo = Number(body.requiredCredits) > 0

      if (error?.errorCode === '11001' || error?.errorCode === '11002' || hasCreditInfo) {
        this.pendingUnlockInfo = body
        this.pendingUnlockInsufficient = error.errorCode === '11002'
          || Number(body.currentCredits) < Number(body.requiredCredits)
        return true
      }

      if (body?.status === 'unlocked' && body?.normal_url && this.pendingUnlockMessage) {
        this.applyMediaRequestUnlock(this.pendingUnlockMessage, body)
        this.aiUnlockModal = false
        this.pendingUnlockMessage = null
        this.pendingUnlockInfo = null
        this.pendingUnlockInsufficient = false
        return true
      }

      return false
    },
    isMediaRequestUnlockInfoResponse(response) {
      return Number(response?.data?.body?.requiredCredits) > 0
    },
    fetchMediaRequestUnlockInfo(requestId) {
      this.aiUnlockInfoLoading = true
      useJwt.chatMediaRequestUnlock(requestId, false).then(response => {
        if (!this.applyMediaRequestUnlockInfo(response)) {
          const message = response?.data?.header?.error?.message
          if (message) throw new Error(message)
        }
      }).catch(err => {
        const response = err?.response
        if (response && this.applyMediaRequestUnlockInfo(response)) return
        throwDefaultError(this.$toast, err)
      }).finally(() => {
        this.aiUnlockInfoLoading = false
      })
    },
    closeAiUnlockModal() {
      if (this.aiUnlockLoading || this.aiUnlockInfoLoading) return
      this.aiUnlockModal = false
      this.pendingUnlockMessage = null
      this.pendingUnlockInfo = null
      this.pendingUnlockInsufficient = false
    },
    unlockPendingAiMedia() {
      if (!this.pendingUnlockMessage) return
      if (this.pendingUnlockInsufficient) {
        this.closeAiUnlockModal()
        this.$router.push({ name: 'market', params: { purchaseSource: 'media_request_unlock' } })
        return
      }
      this.unlockMediaRequest(this.pendingUnlockMessage)
    },
    applyMediaRequestUnlock(msgData, body) {
      const mediaRequest = this.getMediaRequest(msgData)
      if (mediaRequest) {
        this.$set(mediaRequest, 'locked', false)
        this.$set(mediaRequest, 'normal_url', body.normal_url)
        if (body.content_type) {
          this.$set(mediaRequest, 'content_type', body.content_type)
        }
      }

      const atts = msgData.payload && Array.isArray(msgData.payload.attachments) ? msgData.payload.attachments : []
      const att = atts[0]
      if (att) {
        this.$set(att, 'url', body.normal_url)
        this.$set(att, 'type', body.content_type === 'video' ? 'video' : 'image')
        if (att.meta) this.$set(att.meta, 'blur_url', null)
      }

      // Unlock butonu artık anlamsız; mesajdan çıkar
      if (msgData.payload && Array.isArray(msgData.payload.actions)) {
        this.$set(
          msgData.payload,
          'actions',
          msgData.payload.actions.filter(a => a?.params?.intent !== 'MEDIA_REQUEST_UNLOCK'),
        )
      }

      this.$emit('image-loaded')
    },
    isInsufficientCreditsError(err) {
      const error = err?.response?.data?.header?.error
      if (error?.errorCode === '11002') return true

      const message = error?.message || err?.message || ''
      return message.includes('Yetersiz elmas')
        || message.toLowerCase().includes('insufficient')
    },
    unlockMediaRequest(msgData) {
      const requestId = this.getMediaRequestId(msgData)
      if (!requestId) return

      this.aiUnlockLoading = true
      // Kullanıcı modaldaki CTA'ya bastı: elmasla satın alma onayı
      useJwt.chatMediaRequestUnlock(requestId, true).then(response => {
        const error = response?.data?.header?.error
        const body = response?.data?.body

        if (this.isMediaRequestUnlockInfoResponse(response)) {
          this.applyMediaRequestUnlockInfo(response)
          return
        }

        if (body?.status !== 'unlocked' || !body?.normal_url) {
          throw new Error(error?.message || this.$t('Unlock failed'))
        }

        this.applyMediaRequestUnlock(msgData, body)

        this.aiUnlockModal = false
        this.pendingUnlockMessage = null
        this.pendingUnlockInfo = null
        this.pendingUnlockInsufficient = false
      }).catch(err => {
        // Bakiye yetmiyorsa modalı kapatma; CTA'yı "Elmas Al"a çevir
        if (this.isMediaRequestUnlockInfoResponse(err?.response)) {
          this.applyMediaRequestUnlockInfo(err.response)
          return
        }

        if (this.isInsufficientCreditsError(err)) {
          this.pendingUnlockInsufficient = true
          return
        }
        throwDefaultError(this.$toast, err)
      }).finally(() => {
        this.aiUnlockLoading = false
      })
    },
    // payload.meta.game — doküman: action'lar geçici, meta kalıcı
    getGameMeta(msg) {
      const payload = msg?.payload || {}
      return payload?.meta?.game || payload?.game || null
    },
    // Chat mesajındaki "Play" butonu: oyuna katılma isteğini üst bileşene taşı
    openGuulMatch(action, msg) {
      const params = action?.params || {}
      const game = this.getGameMeta(msg) || {}

      this.$emit('open-guul-match', {
        opponentUserId: params.opponentUserId || game.opponentUserId || game.fromUserId || msg?.fromId || null,
        // Doküman: chat mesajından girişte her zaman is_join true
        isJoin: true,
      })
    },
    // RICH_CONTENT_V1 aksiyon listesi
    getRichActions(msg) {
      const actions = msg.payload && Array.isArray(msg.payload.actions) ? msg.payload.actions : []
      return actions
    },
    // AI_FEEDBACK butonları (REPLY_DATA type + AI_FEEDBACK intent)
    getFeedbackActions(msg) {
      return this.getRichActions(msg).filter(
        a => a.type === 'REPLY_DATA' && a.params && a.params.intent === 'AI_FEEDBACK',
      )
    },
    // Diğer aksiyon butonları
    getNonFeedbackActions(msg) {
      return this.getRichActions(msg).filter(
        a => !(a.type === 'REPLY_DATA' && a.params && a.params.intent === 'AI_FEEDBACK')
          && a?.params?.intent !== 'MEDIA_REQUEST_FULFILL',
      )
    },
    removeMediaRequestFulfillAction(msgData) {
      if (!msgData?.payload || !Array.isArray(msgData.payload.actions)) return

      this.$set(
        msgData.payload,
        'actions',
        msgData.payload.actions.filter(a => a?.params?.intent !== 'MEDIA_REQUEST_FULFILL'),
      )
    },
    handleFeedbackAction(action, msgData) {
      const params = action.params || {}
      const value = params.value || action.value || null
      const aiUserId = params.ai_user_id !== undefined ? Number(params.ai_user_id) : null
      this.$emit('send-ai-feedback', {
        msg_id: msgData.msgId,
        value,
        ai_user_id: aiUserId,
      })
    },
    dismissFeedback(msgId) {
      this.$set(this.dismissedFeedbacks, msgId, true)
    },
    openLightbox(attachment) {
      this.lightbox = attachment
    },
    // Esc ve dışarı tıklamayı b-modal kendisi yönetir (kapanınca @hidden ile buraya gelir)
    closeLightbox() {
      this.lightbox = null
    },
    getActionVariant(action) {
      if (!action) return 'outline-secondary'

      // Ürün ve profil butonlarını uygulamanın ana rengine yakın tut
      if (action.type === 'PRODUCT' || action.type === 'PROFILE') return 'primary'

      if (action.style === 'primary') return 'primary'
      if (action.style === 'secondary') return 'outline-primary'
      return 'outline-secondary'
    },
    handleRichAction(action, msg) {
      if (!action) return

      const params = action.params || {}

      if ((action.type === 'EXTERNAL_LINK' || action.type === 'OPEN_WEBVIEW') && params.url) {
        let href = params.url
        if (!href.startsWith('http://') && !href.startsWith('https://')) {
          href = `https://${href}`
        }
        window.open(href, '_blank')
      } else if (action.type === 'UI_POPUP') {
        const popup = {
          title: params.title,
          header_title: params.header_title,
          content: params.content,
          event_uuid: params.event_uuid,
          placement_id: params.placement_id,
          purchase_source: params.purchase_source,
          show: params.show,
        }
        this.updatePopup({ popup: JSON.stringify(popup) })
      } else if (action.type === 'PRODUCT') {
        // PRODUCT aksiyonunda productId_* yerine yalnızca URL'e yönlendir
        this.navigateToProduct(null, params.url)
      } else if (action.type === 'PROFILE') {
        const uuid = params.uuid || params.id
        const userName = params.user_name || params.username
        this.navigateToProfile(uuid, userName)
      } else if (params.intent === 'GUUL_OPEN_MATCH') {
        this.openGuulMatch(action, msg)
      } else if (params.intent === 'MEDIA_REQUEST_UNLOCK') {
        this.openAiUnlockModal(msg)
      } else if (params.intent === 'MEDIA_REQUEST_FULFILL') {
        this.openMediaRequestUpload(msg)
      } else if (action.type === 'REPLY_DATA') {
        // Şimdilik sadece loglayalım; ileride backend'e feedback gönderilebilir
        // eslint-disable-next-line no-console
        console.log('REPLY_DATA action clicked', action, msg)
      }
    },
    // RICH_CONTENT_V1 gövdesindeki tıklamalar (özellikle PRODUCT / PROFILE entity'leri) için handler
    onRichBodyClick(event, msg) {
      const profileTarget = event.target.closest('.rc-entity-profile')
      if (profileTarget) {
        const { uuid, username, userName } = profileTarget.dataset
        const finalUuid = uuid
        const finalUserName = userName || username
        this.navigateToProfile(finalUuid, finalUserName)
        return
      }

      const productTarget = event.target.closest('.rc-entity-product')
      if (!productTarget) return

      const { url } = productTarget.dataset
      if (url) {
        this.navigateToProduct(null, url)
      }
    },
    // Ürün detay sayfasına yönlendirme yardımcı fonksiyonu
    navigateToProduct(productId, fallbackUrl) {
      if (productId && this.$router) {
        this.$router.push({ name: 'buy', params: { productId } })
      } else if (fallbackUrl) {
        let href = fallbackUrl
        if (!href.startsWith('http://') && !href.startsWith('https://')) {
          href = `https://${href}`
        }
        window.open(href, '_blank')
      }
    },
    // Profil sayfasına yönlendirme yardımcı fonksiyonu
    navigateToProfile(uuid, userName) {
      if (!uuid || !this.$router) return
      this.$router.push({
        name: 'users.show',
        params: {
          id: uuid,
        },
      })
    },
    formatCallDuration(seconds) {
      if (!seconds) return ''
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      if (m > 0) return `${m}m ${s}s`
      return `${s}s`
    },
    getCallMainIcon(callPayload) {
      if (!callPayload) return 'PhoneIcon'
      const { status, direction, callType } = callPayload
      const isVideo = isVideoCall(callPayload)

      if (isVideo) {
        if (status === 'reject') return 'VideoOffIcon'
        return 'VideoIcon'
      }

      if (status === 'completed') return direction === 'outgoing' ? 'PhoneOutgoingIcon' : 'PhoneIncomingIcon'
      if (status === 'no_answer') return 'PhoneMissedIcon'
      if (status === 'reject') return 'PhoneOffIcon'
      return 'PhoneIcon'
    },
    getCallBadgeIcon(callPayload) {
      if (!callPayload) return 'PhoneIcon'
      const { status, direction } = callPayload
      if (status === 'no_answer' || status === 'reject') return 'XIcon'
      return direction === 'outgoing' ? 'ArrowUpRightIcon' : 'ArrowDownLeftIcon'
    },
    getCallStatusText(callPayload) {
      const { status, direction, duration } = callPayload
      if (status === 'completed') return duration ? this.formatCallDuration(duration) : '—'
      if (status === 'no_answer') return direction === 'outgoing' ? this.$t('No Answer') : this.$t('Missed Call')
      if (status === 'reject') return direction === 'outgoing' ? this.$t('Rejected') : this.$t('Call Rejected')
      return ''
    },
    updateModalBoostFlow(value) {
      this.modalBoostFlow = value
      if (!value) {
        this.showModalData = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.chat-message {
  color: #fff;

  // Link stilleri
  :deep(.chat-link) {
    color: #87CEEB;
    text-decoration: underline;

    &:hover {
      color: #ADD8E6;
    }
  }

  // Assistant'tan gelen dış linkler için özel stil
  :deep(.external-link) {
    color: #FFD700;
    text-decoration: underline;
    padding: 2px 6px;
    border-radius: 4px;
    background-color: rgba(255, 215, 0, 0.1);
    border: 1px solid rgba(255, 215, 0, 0.3);
    display: inline-block;
    transition: all 0.3s ease;

    &:hover {
      background-color: rgba(255, 215, 0, 0.2);
      border-color: rgba(255, 215, 0, 0.5);
      text-decoration: none;
    }

    i {
      margin-left: 4px;
      font-size: 0.8em;
    }
  }
}

.rich-card {
  // Genel kart stili – uygulamadaki diğer kartlarla uyumlu
  background: #ffffff;
  border-radius: 24px;
  padding: 10px 14px 10px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  color: #111827;
  max-width: 360px;

  &--has-media {
    width: min(320px, calc(100vw - 112px));
    min-width: min(240px, 100%);
    padding: 10px;
  }

  &--request {
    width: auto;
    min-width: 0;
    padding: 0;
    background: transparent;
    box-shadow: none;
  }

  &__media {
    position: relative;
    width: 100%;
    aspect-ratio: 4 / 3;
    min-height: 210px;
    max-height: 280px;
    overflow: hidden;
    border-radius: 18px;
    margin-bottom: 10px;
    background: #d1d5db;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  &__media-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    background: #e5e7eb;
  }

  &__locked {
    position: absolute;
    inset: 0;
    border-radius: 18px;
    overflow: hidden;
    cursor: pointer;
  }

  &__image--unlocked {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__video-wrap {
    position: absolute;
    inset: 0;
    border-radius: 18px;
    overflow: hidden;
    background: #000;
    cursor: pointer;
  }

  &__video--unlocked {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  &__video-mute-btn {
    position: absolute;
    bottom: 10px;
    right: 10px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(6px);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
  }

  &__image--blurred {
    filter: blur(4px);
    transform: scale(1.04);
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }

  &__lock-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
      160deg,
      rgba(20, 0, 40, 0.38) 0%,
      rgba(180, 60, 120, 0.22) 100%
    );
  }

  &__lock-icon {
    color: #ffffff;
    filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.45));
  }

  &__body {
    font-size: 12px;
    line-height: 1.4;
    margin-bottom: 10px;
  }

  &__text {
    color: #111827;
    white-space: pre-line;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__action-btn {
    border-radius: 999px;
    font-size: 11px;
    padding: 4px 12px;
    font-weight: 600;
    line-height: 1.2;
  }

  &__feedback-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  &__feedback-btn {
    border-radius: 999px;
    font-size: 11px;
    padding: 4px 12px;
    font-weight: 600;
    line-height: 1.2;
  }
}


@media (max-width: 575.98px) {
  .rich-card {
    &--has-media {
      width: min(280px, calc(100vw - 96px));
      min-width: min(220px, 100%);
    }

    &__media {
      min-height: 180px;
      max-height: 240px;
    }
  }
}

:deep(.rc-entity-product) {
  font-weight: 700;
  color: #f9447a;
  cursor: pointer;
  text-decoration: underline;
}

// RICH_CONTENT_V1 içindeki link görünümleri
.rich-card__text {
  :deep(a) {
    color: #2563eb;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #1d4ed8;
    }
  }
}

.chat:not(.chat-left) .rich-card:not(.rich-card--has-media):not(.rich-card--request) {
  .rich-card__text {
    color: #ffffff;
  }
}

// RICH_CONTENT_V1 primary butonu – uygulamanın ana pembe rengi
.rich-card__action-btn.btn-primary {
  background-color: #f9447a !important;
  border-color: #f9447a !important;
  color: #ffffff !important;
  background-image: none !important;
}

// ── CHAT CALL BUBBLE (WhatsApp tarzı) ───────────────────────────────────────
.chat-call-bubble {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px 10px 12px;
  border-radius: 36px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
  cursor: default;
  user-select: none;
  min-width: 190px;

  &__icon-wrap {
    position: relative;
    flex-shrink: 0;
    width: 42px;
    height: 42px;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__badge {
    position: absolute;
    bottom: -2px;
    right: -2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    svg { color: #fff !important; }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__title {
    font-family: 'Lexend', sans-serif;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
  }

  &__sub {
    font-family: 'Lexend', sans-serif;
    font-size: 11px;
    font-weight: 300;
    white-space: nowrap;
  }

  // Global .chat-left div { color: #000 !important } kuralını ezmek için
  // daha yüksek specificity ile her iki tarafı eşitle
  .chat-call-bubble__sub {
    color: #9ca3af !important;
  }

  // ════ GİDEN — beyaz arka plan (soldaki ile aynı) ════
  &--right {
    background-color: #F1F1F1 !important;
    background: #F1F1F1 !important;

    .chat-call-bubble__title { color: #111827 !important; }

    &.chat-call-bubble--completed {
      .chat-call-bubble__icon {
        background: rgba(34, 197, 94, 0.15) !important;
        svg { color: #16a34a !important; }
      }
      .chat-call-bubble__badge { background: #16a34a !important; border: 2px solid #F1F1F1 !important; }
      .chat-call-bubble__sub   { color: #9ca3af !important; }
    }

    &.chat-call-bubble--no_answer,
    &.chat-call-bubble--timeout,
    &.chat-call-bubble--reject,
    &.chat-call-bubble--declined,
    &.chat-call-bubble--failed {
      .chat-call-bubble__icon {
        background: rgba(239, 68, 68, 0.12) !important;
        svg { color: #dc2626 !important; }
      }
      .chat-call-bubble__badge { background: #dc2626 !important; border: 2px solid #F1F1F1 !important; }
    }

    &.chat-call-bubble--busy {
      .chat-call-bubble__icon {
        background: rgba(249, 115, 22, 0.12) !important;
        svg { color: #f97316 !important; }
      }
      .chat-call-bubble__badge { background: #f97316 !important; border: 2px solid #F1F1F1 !important; }
    }

    &.chat-call-bubble--cancelled {
      .chat-call-bubble__icon {
        background: rgba(156, 163, 175, 0.15) !important;
        svg { color: #9ca3af !important; }
      }
      .chat-call-bubble__badge { background: #9ca3af !important; border: 2px solid #F1F1F1 !important; }
    }
  }

  // ════ GELEN — gri arka plan, renkli ikonlar ════
  &--left {
    background-color: #F1F1F1 !important;
    background: #F1F1F1 !important;

    .chat-call-bubble__title { color: #111827 !important; }

    // Tamamlandı → yeşil
    &.chat-call-bubble--completed {
      .chat-call-bubble__icon {
        background: rgba(34, 197, 94, 0.15);
        svg { color: #16a34a !important; }
      }
      .chat-call-bubble__badge { background: #16a34a; border: 2px solid #F1F1F1; }
    }

    // Cevapsız / timeout → kırmızı
    &.chat-call-bubble--no_answer,
    &.chat-call-bubble--timeout {
      .chat-call-bubble__icon {
        background: rgba(239, 68, 68, 0.12);
        svg { color: #dc2626 !important; }
      }
      .chat-call-bubble__badge { background: #dc2626; border: 2px solid #F1F1F1; }
    }

    // Reddedildi / declined / failed → kırmızı
    &.chat-call-bubble--reject,
    &.chat-call-bubble--declined,
    &.chat-call-bubble--failed {
      .chat-call-bubble__icon {
        background: rgba(239, 68, 68, 0.12);
        svg { color: #dc2626 !important; }
      }
      .chat-call-bubble__badge { background: #dc2626; border: 2px solid #F1F1F1; }
    }

    // Meşgul → turuncu
    &.chat-call-bubble--busy {
      .chat-call-bubble__icon {
        background: rgba(249, 115, 22, 0.12);
        svg { color: #f97316 !important; }
      }
      .chat-call-bubble__badge { background: #f97316; border: 2px solid #F1F1F1; }
    }

    // İptal → gri
    &.chat-call-bubble--cancelled {
      .chat-call-bubble__icon {
        background: rgba(156, 163, 175, 0.15);
        svg { color: #9ca3af !important; }
      }
      .chat-call-bubble__badge { background: #9ca3af; border: 2px solid #F1F1F1; }
    }
  }
}

.chat-left {
  .chat-message {
    color: #000;

    a {
      text-decoration: underline !important;
    }

    // Sol taraf (kullanıcı) linkleri için farklı renk
    :deep(.chat-link) {
      color: #0066CC;

      &:hover {
        color: #004499;
      }
    }

    // Assistant'tan gelen mesajlarda dış linkler
    :deep(.external-link) {
      color: #FF6B35;
      background-color: rgba(255, 107, 53, 0.1);
      border-color: rgba(255, 107, 53, 0.3);
      text-decoration: underline;

      &:hover {
        background-color: rgba(255, 107, 53, 0.2);
        border-color: rgba(255, 107, 53, 0.5);
      }
    }
  }
}

.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 14px !important;
  min-width: 52px;

  span {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #aaa;
    animation: typing-bounce 1.2s infinite ease-in-out;

    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}

@keyframes typing-bounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-6px); opacity: 1; }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

// ── FOTOĞRAF/VİDEO POPUP ─────────────────────────────────────────────────────
// (popup kabı body'ye taşındığı için kabın stilleri aşağıdaki global stil bloğunda)
.lightbox-media {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 96px);
  max-height: calc(100dvh - 96px);
  margin: 0 auto;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6);
}

.lightbox-close {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(8px);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
}

.rich-card__image--clickable {
  cursor: zoom-in;
}

.rich-card__video-wrap {
  cursor: zoom-in;
}

.media-request-card {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  gap: 12px;
  min-width: min(280px, 100%);
  max-width: 360px;
  padding: 18px;
  border-radius: 18px;
  background: #f3f3f5;
  color: #28282f;
  text-align: left;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 16px;
    color: #b500d8;
    background: rgba(181, 0, 216, 0.08);
  }

  &__content {
    min-width: 0;

    strong,
    span {
      display: block;
    }

    strong {
      color: #111827;
      font-size: 15px;
      line-height: 1.25;
      font-weight: 800;
    }

    span {
      margin-top: 4px;
      color: #7a7a83;
      font-size: 14px;
      line-height: 1.3;
    }
  }

  &__button {
    grid-column: 1 / -1;
    justify-self: start;
    margin-top: 10px;
    border: 0;
    border-radius: 999px;
    padding: 10px 16px;
    color: #fff;
    background: linear-gradient(100deg, #8a2cff 0%, #d100b8 100%);
    font-size: 14px;
    font-weight: 800;

    &:disabled {
      opacity: 0.65;
    }
  }
}

.ai-unlock-modal {
  .modal-dialog {
    max-width: 390px;
  }

  .ai-unlock-modal-content {
    border: 0;
    border-radius: 24px;
    overflow: hidden;
    background: #fff8f1;
    box-shadow: 0 28px 70px rgba(20, 20, 20, 0.35);
  }
}

.ai-unlock-paywall {
  position: relative;
  padding: 32px 22px 24px;
  text-align: center;
  color: #28282f;

  &__close {
    position: absolute;
    top: 18px;
    right: 18px;
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: #6f6f78;
    padding: 0;
  }

  &__hero {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 70px;
    height: 52px;
    margin-bottom: 18px;
    border-radius: 16px;
    color: #fff;
    background: linear-gradient(135deg, #744bff 0%, #8b5cff 100%);
    transform: rotate(-7deg);
    box-shadow: 0 18px 38px rgba(116, 75, 255, 0.28);

    span {
      position: absolute;
      right: -12px;
      bottom: -8px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      color: #fff;
      background: #ff6848;
      border: 4px solid #fff8f1;
      transform: rotate(7deg);
    }
  }

  h2 {
    max-width: 320px;
    margin: 0 auto 12px;
    font-size: 24px;
    line-height: 1.18;
    font-weight: 800;
  }

  p {
    max-width: 320px;
    margin: 0 auto 18px;
    color: #76727a;
    font-size: 15px;
    line-height: 1.3;
  }

  &__row,
  &__balance {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
    padding: 14px 16px;
    border-radius: 18px;
    background: rgba(255, 103, 72, 0.08);
    text-align: left;
  }

  &__media-icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 14px;
    color: #fff;
    background: #ff6848;
  }

  &__row-main {
    flex: 1 1 auto;

    span,
    strong {
      display: block;
    }

    span {
      color: #76727a;
      font-size: 13px;
      line-height: 1.2;
    }

    strong {
      color: #ff6848;
      font-size: 18px;
      line-height: 1.15;
      font-weight: 800;
    }
  }

  &__row-side {
    flex: 0 0 auto;
    text-align: right;

    strong,
    span {
      display: block;
    }

    strong {
      color: #ff6848;
      font-size: 15px;
      font-weight: 800;
    }

    span {
      color: #76727a;
      font-size: 13px;
    }
  }

  &__balance {
    background: rgba(255, 103, 72, 0.07);

    span,
    strong {
      display: block;
    }

    span {
      color: #76727a;
      font-size: 15px;
    }

    strong {
      color: #28282f;
      font-size: 26px;
      line-height: 1.1;
      font-weight: 800;
    }
  }

  &__cta {
    align-items: center;
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr) 22px;
    gap: 12px;
    width: 100%;
    margin-top: 4px;
    border: 0;
    border-radius: 18px;
    padding: 14px 16px;
    color: #fff;
    text-align: left;
    background: linear-gradient(100deg, #ff5a32 0%, #ff7b68 100%);
    box-shadow: 0 16px 30px rgba(255, 90, 50, 0.24);

    &:disabled {
      opacity: 0.75;
    }

    span {
      min-width: 0;
    }

    strong,
    small,
    em {
      display: block;
      font-style: normal;
    }

    strong {
      font-size: 16px;
      line-height: 1.15;
      font-weight: 800;
    }

    small {
      color: #fff;
      font-size: 15px;
      line-height: 1.18;
      font-weight: 800;
    }

    em {
      color: rgba(255, 255, 255, 0.86);
      font-size: 13px;
      line-height: 1.25;
    }
  }

  &__note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-top: 16px;
    color: #77727a;
    font-size: 13px;
    line-height: 1.3;
    text-align: left;
  }
}

@media screen and (max-width: 575px) {
  .ai-unlock-modal {
    .modal-dialog {
      margin: 0 18px;
    }
  }

  .ai-unlock-paywall {
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    padding: 28px 18px 22px;

    h2 {
      font-size: 21px;
    }

    p {
      font-size: 14px;
    }

    &__row {
      display: grid;
      grid-template-columns: 44px minmax(0, 1fr) auto;
      align-items: center;
    }

    &__cta {
      grid-template-columns: 40px minmax(0, 1fr) 20px;
    }
  }
}
</style>

<style lang="scss">
// Fotoğraf/video popup kabı (body'ye taşınan b-modal): koyu arka plan, kenarsız, içerik ortada.
// Masaüstünde içerik en fazla ekranın %92'si; telefonda tam ekran.
.chat-media-modal {
  .modal-dialog {
    max-width: min(1100px, 92vw);
    // Şablonun genel CSS'i geniş modal'lara %3 sabit yan boşluk veriyor ve popup sola yaslanıyordu
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .chat-media-modal__content {
    background: transparent;
    border: 0;
    box-shadow: none;
  }

  .chat-media-modal__body {
    position: relative;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ~ .modal-backdrop {
    background-color: #000 !important;
    opacity: 0.88 !important;
  }
}

@media (max-width: 575.98px) {
  [dir] .modal.chat-media-modal {
    padding: 0 !important;
  }

  .chat-media-modal {
    .modal-dialog {
      max-width: 100%;
      width: 100%;
      height: 100%;
      margin: 0;
    }

    .chat-media-modal__content {
      height: 100%;
    }

    .chat-media-modal__body {
      height: 100%;
      padding: 56px 8px 16px;
    }

    .lightbox-media {
      border-radius: 8px;
    }
  }
}

/* Modal body'ye taşındığı (static değil) için wrapper stilleri global olmalı */
.ai-unlock-modal {
  .modal-dialog {
    max-width: 390px;
  }

  .ai-unlock-modal-content {
    border: 0;
    border-radius: 24px;
    overflow: hidden;
    background: #fff8f1;
    box-shadow: 0 28px 70px rgba(20, 20, 20, 0.35);
  }
}

@media screen and (max-width: 575px) {
  .ai-unlock-modal {
    .modal-dialog {
      margin: 0 18px;
    }
  }
}
</style>
